// ErrorPage.jsx
import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container,
  Paper,
  useTheme,
  alpha
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate } from 'react-router-dom';

// Keyframe Animations
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const wave = keyframes`
  0% { transform: rotate(0deg); }
  25% { transform: rotate(-10deg); }
  75% { transform: rotate(10deg); }
  100% { transform: rotate(0deg); }
`;

const dash = keyframes`
  to {
    stroke-dashoffset: 0;
  }
`;

const fillIn = keyframes`
  from {
    fill-opacity: 0;
  }
  to {
    fill-opacity: 1;
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const explode = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.5); opacity: 0.5; }
  100% { transform: scale(1); opacity: 1; }
`;

const spiral = keyframes`
  0% { transform: rotate(0deg) translateX(0) scale(1); }
  50% { transform: rotate(360deg) translateX(20px) scale(0.5); }
  100% { transform: rotate(720deg) translateX(0) scale(1); }
`;

const morph = keyframes`
  0% { d: path('M150,150 m-20,0 a20,20 0 1,0 40,0 a20,20 0 1,0 -40,0'); }
  50% { d: path('M150,150 m-40,0 a20,20 0 1,0 80,0 a20,20 0 1,0 -80,0'); }
  100% { d: path('M150,150 m-20,0 a20,20 0 1,0 40,0 a20,20 0 1,0 -40,0'); }
`;

const burst = keyframes`
  0% { transform: scale(0); opacity: 1; }
  50% { transform: scale(1.5); opacity: 0.8; }
  100% { transform: scale(2); opacity: 0; }
`;

const popUp = keyframes`
  0% { transform: scale(0) rotate(0deg); opacity: 0; }
  60% { transform: scale(1.2) rotate(360deg); opacity: 1; }
  100% { transform: scale(1) rotate(360deg); opacity: 1; }
`;

const scatter = keyframes`
  0% { transform: translate(0, 0) scale(1); opacity: 1; }
  100% { transform: translate(var(--tx), var(--ty)) scale(0); opacity: 0; }
`;

// Styled Components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  borderRadius: theme.spacing(3),
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.95)})`,
  backdropFilter: 'blur(10px)',
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
  position: 'relative',
  overflow: 'hidden',
  textAlign: 'center',
  margin: '0 auto',
  '&:active': {
    transform: 'scale(0.98)',
  },
  transition: 'transform 0.2s ease',
}));

const FloatingElement = styled('div')({
  animation: `${float} 3s ease-in-out infinite`,
});

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  padding: theme.spacing(1.5, 4),
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1.1rem',
  transition: 'all 0.3s ease',
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 5px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
}));

const AnimatedSvgContainer = styled('div')(({ theme, isAnimating }) => ({
  position: 'relative',
  '& svg': {
    transition: 'all 0.3s ease',
    transform: isAnimating ? 'scale(0.9)' : 'scale(1)',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  '& .interactive-elements circle': {
    transition: 'all 0.3s ease',
    '&:hover': {
      filter: 'brightness(1.2)',
    },
  },
}));

const ExplosionParticle = styled('div')(({ theme, angle, delay }) => ({
  position: 'absolute',
  width: '4px',
  height: '4px',
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  opacity: 0,
  pointerEvents: 'none',
}));

const BurstParticle = styled('div')(({ theme, color }) => ({
  position: 'absolute',
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  backgroundColor: color || theme.palette.primary.main,
  pointerEvents: 'none',
}));

const PopElement = styled('div')(({ theme }) => ({
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  color: 'white',
  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
  pointerEvents: 'none',
}));

const ErrorPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [isAnimating, setIsAnimating] = useState(false);
  const [burstElements, setBurstElements] = useState([]);
  const [popUps, setPopUps] = useState([]);

  const createBurst = (x, y) => {
    // Create burst particles
    const particles = Array.from({ length: 12 }).map((_, i) => ({
      id: `burst-${Date.now()}-${i}`,
      angle: (i * 30) + Math.random() * 30,
      speed: 100 + Math.random() * 100,
      color: i % 2 === 0 ? theme.palette.primary.main : theme.palette.secondary.main,
      x,
      y,
    }));

    setBurstElements(particles);

    // Create pop-up elements
    const newPopUps = [
      {
        id: `pop-${Date.now()}`,
        text: '404',
        x: x - 30,
        y: y - 30,
        delay: 0,
      },
      {
        id: `pop-${Date.now()}-2`,
        text: 'Oops!',
        x: x + 30,
        y: y + 30,
        delay: 200,
      },
    ];

    setPopUps(newPopUps);

    // Clean up animations
    setTimeout(() => {
      setBurstElements([]);
      setPopUps([]);
    }, 3000);
  };

  const handleClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
   // setIsAnimating(true);
    createBurst(x, y);

  };

  return (
    <Container maxWidth="md" sx={{ height: '100vh', display: 'flex', alignItems: 'center' }}>
      <StyledPaper elevation={0}>
        <Box 
          sx={{ 
            position: 'relative', 
            width: '100%', 
            height: '100%',
            cursor: 'pointer',
            overflow: 'hidden'
          }}
          onClick={handleClick}
        >
          {/* Burst Particles */}
          {burstElements.map((particle) => (
            <BurstParticle
              key={particle.id}
              color={particle.color}
              sx={{
                left: particle.x,
                top: particle.y,
                '--tx': `${Math.cos(particle.angle * Math.PI / 180) * particle.speed}px`,
                '--ty': `${Math.sin(particle.angle * Math.PI / 180) * particle.speed}px`,
                animation: `${scatter} 0.8s ease-out forwards`,
              }}
            />
          ))}

          {/* Pop-up Elements */}
          {popUps.map((popup) => (
            <PopElement
              key={popup.id}
              sx={{
                left: popup.x,
                top: popup.y,
                animation: `${popUp} 0.5s ease-out forwards`,
                animationDelay: `${popup.delay}ms`,
                opacity: 0,
              }}
            >
              {popup.text}
            </PopElement>
          ))}

          <FloatingElement>
            <AnimatedSvgContainer isAnimating={isAnimating}>
              <svg
                width="300"
                height="300"
                viewBox="0 0 300 300"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ overflow: 'visible' }}
              >
                {/* Background Circle with rotating gradient */}
                <defs>
                  <linearGradient id="circleGradient" gradientTransform="rotate(0)">
                    <stop offset="0%" stopColor={theme.palette.primary.main} />
                    <stop offset="100%" stopColor={theme.palette.secondary.main} />
                    <animateTransform
                      attributeName="gradientTransform"
                      type="rotate"
                      from="0 150 150"
                      to="360 150 150"
                      dur="10s"
                      repeatCount="indefinite"
                    />
                  </linearGradient>
                </defs>

                {/* Outer rotating circles */}
                <g style={{ animation: `${rotate} 20s linear infinite` }}>
                  {[...Array(8)].map((_, i) => (
                    <circle
                      key={i}
                      cx="150"
                      cy="150"
                      r={120 + i * 5}
                      stroke={`${theme.palette.primary.main}${20 - i * 2}`}
                      strokeWidth="0.5"
                      fill="none"
                    />
                  ))}
                </g>

                {/* Main circle with dash animation */}
                <circle
                  cx="150"
                  cy="150"
                  r="100"
                  stroke="url(#circleGradient)"
                  strokeWidth="4"
                  fill="none"
                  style={{
                    strokeDasharray: 628,
                    strokeDashoffset: 628,
                    animation: `${dash} 2s ease-out forwards`
                  }}
                />

                {/* Decorative paths */}
                <path
                  d="M100,150 Q150,200 200,150"
                  stroke={theme.palette.secondary.main}
                  strokeWidth="4"
                  fill="none"
                  strokeLinecap="round"
                  style={{
                    strokeDasharray: 150,
                    strokeDashoffset: 150,
                    animation: `${dash} 1s ease-out forwards 1s`
                  }}
                />

                {/* Eyes with fill animation */}
                <g style={{ animation: `${fillIn} 0.5s ease-out forwards 2s` }}>
                  <path
                    d="M120,130 Q130,120 140,130 Q130,140 120,130"
                    fill={theme.palette.primary.main}
                    fillOpacity="0"
                  >
                    <animate
                      attributeName="d"
                      dur="2s"
                      repeatCount="indefinite"
                      values="
                        M120,130 Q130,120 140,130 Q130,140 120,130;
                        M120,125 Q130,115 140,125 Q130,135 120,125;
                        M120,130 Q130,120 140,130 Q130,140 120,130
                      "
                    />
                  </path>
                  <path
                    d="M160,130 Q170,120 180,130 Q170,140 160,130"
                    fill={theme.palette.primary.main}
                    fillOpacity="0"
                  >
                    <animate
                      attributeName="d"
                      dur="2s"
                      repeatCount="indefinite"
                      values="
                        M160,130 Q170,120 180,130 Q170,140 160,130;
                        M160,125 Q170,115 180,125 Q170,135 160,125;
                        M160,130 Q170,120 180,130 Q170,140 160,130
                      "
                    />
                  </path>
                </g>

                {/* Interactive elements */}
                <g className="interactive-elements">
                  {[...Array(12)].map((_, i) => (
                    <circle
                      key={i}
                      cx={150 + Math.cos(i * Math.PI / 6) * 90}
                      cy={150 + Math.sin(i * Math.PI / 6) * 90}
                      r="4"
                      fill={theme.palette.secondary.main}
                      style={{
                        animation: `${pulse} ${1 + i * 0.2}s ease-in-out infinite`,
                        transformOrigin: 'center',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.fill = theme.palette.primary.main;
                        e.target.style.r = "6";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.fill = theme.palette.secondary.main;
                        e.target.style.r = "4";
                      }}
                    />
                  ))}
                </g>

                {/* Animated decorative lines */}
                {[...Array(8)].map((_, i) => (
                  <path
                    key={i}
                    d={`M150,150 L${150 + Math.cos(i * Math.PI / 4) * 140},${150 + Math.sin(i * Math.PI / 4) * 140}`}
                    stroke={`${theme.palette.primary.main}40`}
                    strokeWidth="1"
                    style={{
                      strokeDasharray: 140,
                      strokeDashoffset: 140,
                      animation: `${dash} 1s ease-out forwards ${i * 0.1}s`
                    }}
                  />
                ))}

                {/* Central animated pattern */}
                <g style={{ animation: `${rotate} 10s linear infinite` }}>
                  <path
                    d="M150,150 m-20,0 a20,20 0 1,0 40,0 a20,20 0 1,0 -40,0"
                    fill="none"
                    stroke={theme.palette.secondary.main}
                    strokeWidth="2"
                    style={{
                      animation: `${pulse} 2s ease-in-out infinite`
                    }}
                  />
                </g>

                {/* Add these new elements for click animation */}
                <g style={{ 
                  transform: isAnimating ? 'scale(1.2)' : 'scale(1)',
                  transition: 'transform 0.5s ease'
                }}>
                  {/* Explosion rings */}
                  {isAnimating && (
                    <>
                      <circle
                        cx="150"
                        cy="150"
                        r="60"
                        stroke={theme.palette.primary.main}
                        strokeWidth="2"
                        fill="none"
                        style={{
                          animation: `${explode} 1s ease-out forwards`,
                        }}
                      />
                      <circle
                        cx="150"
                        cy="150"
                        r="80"
                        stroke={theme.palette.secondary.main}
                        strokeWidth="2"
                        fill="none"
                        style={{
                          animation: `${explode} 1s ease-out forwards 0.2s`,
                        }}
                      />
                    </>
                  )}

                  {/* Spiral elements */}
                  {isAnimating && (
                    <g>
                      {[...Array(8)].map((_, i) => (
                        <path
                          key={`spiral-${i}`}
                          d={`M150,150 m${i * 10},0 a${i * 5},${i * 5} 0 1,0 ${i * 2},0`}
                          stroke={theme.palette.primary.main}
                          strokeWidth="1"
                          fill="none"
                          style={{
                            animation: `${spiral} ${1 + i * 0.1}s ease-out forwards`,
                          }}
                        />
                      ))}
                    </g>
                  )}

                  {/* Morphing shape */}
                  <path
                    d="M150,150 m-20,0 a20,20 0 1,0 40,0 a20,20 0 1,0 -40,0"
                    fill="none"
                    stroke={theme.palette.secondary.main}
                    strokeWidth="2"
                    style={{
                      animation: isAnimating ? `${morph} 1s ease-in-out forwards` : 'none',
                    }}
                  />
                </g>
              </svg>
            </AnimatedSvgContainer>
          </FloatingElement>
        </Box>

        <Typography 
          variant="h2" 
          gutterBottom
          sx={{
            fontWeight: 800,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            mb: 2,
          }}
        >
          404
        </Typography>

        <Typography 
          variant="h4" 
          gutterBottom
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            mb: 3,
          }}
        >
          Oops! Page Not Found
        </Typography>

        <Typography 
          variant="body1" 
          sx={{ 
            color: theme.palette.text.secondary,
            mb: 4,
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          The page you're looking for seems to have wandered off. Don't worry, even the best of us get lost sometimes!
        </Typography>

        <ActionButton
          variant="contained"
          startIcon={<HomeIcon />}
          onClick={() => navigate('/')}
        >
          Back to Home
        </ActionButton>

        {/* Background Decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: -1,
            opacity: 0.05,
            overflow: 'hidden',
          }}
        >
          {[...Array(6)].map((_, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                animation: `${float} ${3 + i}s ease-in-out infinite`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                transform: 'scale(2)',
              }}
            />
          ))}
        </Box>
      </StyledPaper>
    </Container>
  );
};

export default ErrorPage;
